import type { TokenDeOrigem } from '../types/origemDoAcessoTypes';

/**
 * Nome do parâmetro na URL. Curto porque o link vai no sufixo do botão do WhatsApp e no SMS de
 * reserva do RCS, onde cada caractere conta.
 */
export const PARAMETRO_DA_ORIGEM = 'o';

/**
 * 256 bits em base64url sem padding: o formato que o core sorteia por envio. O core repete a
 * validação e ignora o que não casa; aqui ela evita repassar adiante, na URL e no corpo da
 * chamada, um valor que nunca seria token.
 */
const FORMATO_DO_TOKEN_DE_ORIGEM = /^[A-Za-z0-9_-]{43}$/;

/**
 * `?o=` lido da URL. Valor fora do formato é descartado em silêncio: link sem token, com token
 * malformado ou de outro item abre o portal do mesmo jeito, só sem origem.
 *
 * O token mora só na URL até a sessão nascer, e nunca em Redux, `sessionStorage` ou telemetria:
 * a origem que vale depois disso é a que o core guardou na sessão.
 *
 * @example lerOrigemDoLink(searchParams.get(PARAMETRO_DA_ORIGEM)) // token | undefined
 */
export function lerOrigemDoLink(valor: string | null): TokenDeOrigem | undefined {
  return valor !== null && FORMATO_DO_TOKEN_DE_ORIGEM.test(valor) ? valor : undefined;
}

/**
 * Anexa o token ao caminho, preservando a query que ele já tiver. Sem token, devolve o caminho
 * intacto e nunca gera `o=` vazio. Base64url não precisa de escape na query.
 *
 * @example comOrigemDoLink('/auth/signup?portalHash=h1', token) // '/auth/signup?portalHash=h1&o=…'
 */
export function comOrigemDoLink(caminho: string, origem: TokenDeOrigem | undefined): string {
  if (!origem) return caminho;
  return `${caminho}${caminho.includes('?') ? '&' : '?'}${PARAMETRO_DA_ORIGEM}=${origem}`;
}

/**
 * Tira o token de origem de uma URL absoluta antes que ela saia do navegador num evento de
 * rastreamento: o core grava `currentUrl` e `referrer` e os imprime no certificado. Só o par `o`
 * sai; o resto da query e o hash ficam byte a byte, porque `portalHash` e o código são prova.
 * Filtra os pares crus em vez de reserializar `URLSearchParams`, que trocaria `%20` por `+` nos
 * outros parâmetros. URL inválida, vazia ou sem `o` volta idêntica.
 *
 * @example semOrigemDoLink('https://x/auth/signup?portalHash=h1&o=…#a') // 'https://x/auth/signup?portalHash=h1#a'
 */
export function semOrigemDoLink(url: string): string {
  let analisada: URL;
  try {
    analisada = new URL(url);
  } catch {
    return url;
  }
  const pares = analisada.search.slice(1).split('&');
  const restantes = pares.filter((par) => !ehParDaOrigem(par));
  if (restantes.length === pares.length) return url;
  analisada.search = restantes.join('&');
  return analisada.href;
}

function ehParDaOrigem(par: string): boolean {
  return new URLSearchParams(par).has(PARAMETRO_DA_ORIGEM);
}
